// components/radiologists/hooks/useRadiologists.js

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { userAPI, authAPI } from '../../../services/api';
import { radiologistSchema, registerSchema } from '../../../validations/schemas';
import { useAuth } from '../../../context/AuthContext';

export const useRadiologists = () => {
    const { t } = useTranslation();
    const { user, isSuperAdmin } = useAuth();

    // State
    const [radiologists, setRadiologists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedRadiologist, setSelectedRadiologist] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [radiologistToDelete, setRadiologistToDelete] = useState(null);
    const [privilegeError, setPrivilegeError] = useState(false);

    // Export state
    const [exporting, setExporting] = useState(false);
    const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

    // Pagination State
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 9,
        total: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPrevPage: false
    });

    // Filter state
    const [filters, setFilters] = useState({
        search: '',
        status: 'all',
        gender: '',
        branch: '',
        sortBy: 'name',
        sortOrder: 'asc',
    });

    // Permissions
    const permissions = {
        canView: isSuperAdmin || user?.privileges?.some(p => p.module === 'radiologists' && p.operations.includes('view')),
        canAdd: isSuperAdmin || user?.privileges?.some(p => p.module === 'radiologists' && p.operations.includes('create')),
        canEdit: isSuperAdmin || user?.privileges?.some(p => p.module === 'radiologists' && p.operations.includes('update')),
        canDelete: isSuperAdmin || user?.privileges?.some(p => p.module === 'radiologists' && p.operations.includes('delete')),
        canExport: isSuperAdmin || user?.privileges?.some(p => p.module === 'radiologists' && p.operations.includes('view'))
    };

    // Check if any filter is active
    const hasActiveFilters = filters.search || filters.status !== 'all' || filters.gender || filters.branch;

    // Form initial values
    const getInitialValues = () => {
        if (selectedRadiologist) {
            return {
                username: selectedRadiologist.username || '',
                name: selectedRadiologist.name || '',
                gender: selectedRadiologist.gender || '',
                age: selectedRadiologist.age || '',
                phoneNumber: selectedRadiologist.phoneNumber ? selectedRadiologist.phoneNumber.replace('+20', '') : '',
                isActive: selectedRadiologist.isActive !== undefined ? selectedRadiologist.isActive : true,
            };
        }
        return {
            username: '',
            name: '',
            gender: '',
            age: '',
            phoneNumber: '',
            isActive: true,
            password: '',
            confirmPassword: '',
        };
    };

    // Formik setup
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: getInitialValues(),
        validationSchema: radiologistSchema.shape({
            password: selectedRadiologist ? undefined : registerSchema.fields.password,
            confirmPassword: selectedRadiologist ? undefined : registerSchema.fields.confirmPassword,
            username: registerSchema.fields.username,
        }),
        onSubmit: async (values, { resetForm }) => {
            try {
                setLoading(true);

                if (selectedRadiologist) {
                    const updatePayload = { ...values };
                    delete updatePayload.password;
                    delete updatePayload.confirmPassword;
                    delete updatePayload.id;

                    if (updatePayload.phoneNumber) {
                        const phone = updatePayload.phoneNumber.replace(/\D/g, '');
                        updatePayload.phoneNumber = `+20${phone}`;
                    }

                    await userAPI.updateRadiologist(selectedRadiologist._id, updatePayload);
                    toast.success(t('radiologists.updateSuccess'));
                } else {
                    const registrationData = { ...values, role: 'radiologist' };
                    delete registrationData.confirmPassword;

                    if (registrationData.phoneNumber) {
                        let phone = registrationData.phoneNumber.replace(/\D/g, '');
                        registrationData.phoneNumber = `+20${phone}`;
                    }

                    await authAPI.register(registrationData);
                    toast.success(t('radiologists.createSuccess'));
                }
                handleCloseDialog();
                fetchRadiologists();
                resetForm();
            } catch (err) {
                toast.error(err.response?.data?.message || t('radiologists.error'));
            } finally {
                setLoading(false);
            }
        },
    });

    // Fetch radiologists with filters
    const fetchRadiologists = useCallback(async () => {
        if (!permissions.canView) return;

        setLoading(true);
        setPrivilegeError(false);

        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                sort: filters.sortOrder === 'desc' ? `-${filters.sortBy}` : filters.sortBy,
            };

            if (filters.search) {
                params.search = filters.search;
            }

            if (filters.status !== 'all') {
                params.isActive = filters.status === 'active';
            }

            if (filters.gender) {
                params.gender = filters.gender;
            }

            if (filters.branch) {
                params.branch = filters.branch;
            }

            const response = await userAPI.getRadiologists(params);
            const responseData = response?.data;
            const list = Array.isArray(responseData?.data) ? responseData.data : [];
            const paginationInfo = responseData?.pagination;

            setRadiologists(list);
            setPagination(prev => ({
                ...prev,
                total: paginationInfo?.total || list.length,
                totalPages: paginationInfo?.pages || 1,
                hasNextPage: paginationInfo?.hasNextPage || false,
                hasPrevPage: paginationInfo?.hasPrevPage || false
            }));

        } catch (err) {
            const backendMessage = err.response?.data?.message;
            if (backendMessage && backendMessage.toLowerCase().includes('insufficient privileges')) {
                setPrivilegeError(true);
            } else {
                setRadiologists([]);
                toast.error(backendMessage || t('radiologists.error'));
            }
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, filters, permissions.canView, t]);

    // Initial fetch and filter changes
    useEffect(() => {
        fetchRadiologists();
    }, [fetchRadiologists]);

    // Dialog handlers
    const handleOpenDialog = (radiologist = null) => {
        setSelectedRadiologist(radiologist);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedRadiologist(null);
        formik.resetForm();
    };

    // Delete handlers
    const handleDeleteClick = (radiologist) => {
        setRadiologistToDelete(radiologist);
        setDeleteConfirmOpen(true);
    };

    const handleDelete = async (id) => {
        if (!id) return;

        try {
            setLoading(true);
            await userAPI.delete(id);
            toast.success(t('radiologists.deleteSuccess'));
            setDeleteConfirmOpen(false);
            setRadiologistToDelete(null);
            fetchRadiologists();
        } catch (err) {
            toast.error(err.response?.data?.message || t('radiologists.deleteError'));
        } finally {
            setLoading(false);
        }
    };

    // Pagination handlers
    const handlePageChange = (event, newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleLimitChange = (event) => {
        setPagination(prev => ({
            ...prev,
            limit: event.target.value,
            page: 1
        }));
    };

    // Filter handlers
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({ ...prev, [filterName]: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleSearchChange = (value) => {
        handleFilterChange('search', value);
    };

    const handleStatusChange = (event) => {
        handleFilterChange('status', event.target.value);
    };

    const handleGenderChange = (event) => {
        handleFilterChange('gender', event.target.value);
    };

    const handleBranchChange = (event) => {
        handleFilterChange('branch', event.target.value);
    };

    const handleSortChange = (sortBy, sortOrder) => {
        setFilters(prev => ({ ...prev, sortBy, sortOrder }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const clearFilters = () => {
        setFilters({
            search: '',
            status: 'all',
            gender: '',
            branch: '',
            sortBy: 'name',
            sortOrder: 'asc',
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Export menu handlers
    const handleExportMenuOpen = (event) => {
        setExportMenuAnchor(event.currentTarget);
    };

    const handleExportMenuClose = () => {
        setExportMenuAnchor(null);
    };

    // Build export params from current filters
    const buildExportParams = () => {
        const params = {};

        if (filters.search) {
            params.search = filters.search;
        }

        if (filters.status !== 'all') {
            params.isActive = filters.status === 'active';
        }

        if (filters.gender) {
            params.gender = filters.gender;
        }

        if (filters.branch) {
            params.branch = filters.branch;
        }

        if (filters.sortBy) {
            params.sortBy = filters.sortBy;
        }

        if (filters.sortOrder) {
            params.sortOrder = filters.sortOrder;
        }

        return params;
    };

    // Download blob helper
    const downloadBlob = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    // Get filename from response headers or use default
    const getFilenameFromResponse = (response, defaultName) => {
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
                return filenameMatch[1].replace(/['"]/g, '');
            }
        }
        return defaultName;
    };

    // Export handlers
    const handleExportSimple = async () => {
        if (!permissions.canExport) {
            toast.error(t('common.noExportPermission'));
            return;
        }

        setExporting(true);
        handleExportMenuClose();

        try {
            const params = { ...buildExportParams(), exportFormat: 'simple' };
            const response = await userAPI.exportRadiologists(params);
            const filename = getFilenameFromResponse(response, 'radiologists_export.xlsx');
            downloadBlob(response.data, filename);
            toast.success(t('radiologists.exportSuccess') || 'Export successful');
        } catch (err) {
            console.error('Export error:', err);
            toast.error(err.response?.data?.message || t('radiologists.exportError') || 'Export failed');
        } finally {
            setExporting(false);
        }
    };

    const handleExportDetailed = async () => {
        if (!permissions.canExport) {
            toast.error(t('common.noExportPermission'));
            return;
        }

        setExporting(true);
        handleExportMenuClose();

        try {
            const params = buildExportParams();
            const response = await userAPI.exportRadiologistsDetailed(params);
            const filename = getFilenameFromResponse(response, 'radiologists_detailed_export.xlsx');
            downloadBlob(response.data, filename);
            toast.success(t('radiologists.exportSuccess') || 'Export successful');
        } catch (err) {
            console.error('Export error:', err);
            toast.error(err.response?.data?.message || t('radiologists.exportError') || 'Export failed');
        } finally {
            setExporting(false);
        }
    };

    const handleExportByGender = async () => {
        if (!permissions.canExport) {
            toast.error(t('common.noExportPermission'));
            return;
        }

        setExporting(true);
        handleExportMenuClose();

        try {
            const params = buildExportParams();
            const response = await userAPI.exportRadiologistsByGender(params);
            const filename = getFilenameFromResponse(response, 'radiologists_by_gender_export.xlsx');
            downloadBlob(response.data, filename);
            toast.success(t('radiologists.exportSuccess') || 'Export successful');
        } catch (err) {
            console.error('Export error:', err);
            toast.error(err.response?.data?.message || t('radiologists.exportError') || 'Export failed');
        } finally {
            setExporting(false);
        }
    };

    const handleExportByBranch = async () => {
        if (!permissions.canExport) {
            toast.error(t('common.noExportPermission'));
            return;
        }

        setExporting(true);
        handleExportMenuClose();

        try {
            const params = buildExportParams();
            const response = await userAPI.exportRadiologistsByBranch(params);
            const filename = getFilenameFromResponse(response, 'radiologists_by_branch_export.xlsx');
            downloadBlob(response.data, filename);
            toast.success(t('radiologists.exportSuccess') || 'Export successful');
        } catch (err) {
            console.error('Export error:', err);
            toast.error(err.response?.data?.message || t('radiologists.exportError') || 'Export failed');
        } finally {
            setExporting(false);
        }
    };

    const handleExportByStatus = async () => {
        if (!permissions.canExport) {
            toast.error(t('common.noExportPermission'));
            return;
        }

        setExporting(true);
        handleExportMenuClose();

        try {
            const params = buildExportParams();
            const response = await userAPI.exportRadiologistsByStatus(params);
            const filename = getFilenameFromResponse(response, 'radiologists_by_status_export.xlsx');
            downloadBlob(response.data, filename);
            toast.success(t('radiologists.exportSuccess') || 'Export successful');
        } catch (err) {
            console.error('Export error:', err);
            toast.error(err.response?.data?.message || t('radiologists.exportError') || 'Export failed');
        } finally {
            setExporting(false);
        }
    };

    // Generic export with format selection
    const handleExport = async (exportFormat = 'simple') => {
        if (!permissions.canExport) {
            toast.error(t('common.noExportPermission'));
            return;
        }

        setExporting(true);
        handleExportMenuClose();

        try {
            const params = { ...buildExportParams(), exportFormat };
            const response = await userAPI.exportRadiologists(params);
            const filename = getFilenameFromResponse(response, `radiologists_${exportFormat}_export.xlsx`);
            downloadBlob(response.data, filename);
            toast.success(t('radiologists.exportSuccess') || 'Export successful');
        } catch (err) {
            console.error('Export error:', err);
            toast.error(err.response?.data?.message || t('radiologists.exportError') || 'Export failed');
        } finally {
            setExporting(false);
        }
    };

    return {
        // State
        radiologists,
        loading,
        openDialog,
        selectedRadiologist,
        deleteConfirmOpen,
        radiologistToDelete,
        privilegeError,
        permissions,
        pagination,
        filters,
        hasActiveFilters,
        exporting,
        exportMenuAnchor,

        // Form
        formik,

        // Dialog handlers
        handleOpenDialog,
        handleCloseDialog,

        // Delete handlers
        handleDelete,
        handleDeleteClick,
        setDeleteConfirmOpen,

        // Pagination handlers
        handlePageChange,
        handleLimitChange,

        // Filter handlers
        handleFilterChange,
        handleSearchChange,
        handleStatusChange,
        handleGenderChange,
        handleBranchChange,
        handleSortChange,
        clearFilters,

        // Export handlers
        handleExportMenuOpen,
        handleExportMenuClose,
        handleExport,
        handleExportSimple,
        handleExportDetailed,
        handleExportByGender,
        handleExportByBranch,
        handleExportByStatus,

        // Refetch
        fetchRadiologists,
    };
};